import type { Locale } from '@oxyflows/i18n';

/**
 * Display formatting for money in the browser.
 *
 * The API always sends integer MINOR units plus a currency code, and this is
 * the only place they become a human-readable string. Nothing in the web app
 * does arithmetic on a formatted value — amounts stay integers until the moment
 * they are drawn (spec §10).
 */

const EXPONENT: Record<string, number> = {
  USD: 2, EUR: 2, SAR: 2, AED: 2, SYP: 2, TRY: 2, JOD: 3, KWD: 3,
};

export function formatMinor(minor: number, currency: string, locale: Locale): string {
  const exponent = EXPONENT[currency] ?? 2;
  const major = minor / 10 ** exponent;
  try {
    return new Intl.NumberFormat(locale === 'ar' ? 'ar-SY-u-nu-latn' : 'en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: exponent,
      maximumFractionDigits: exponent,
    }).format(major);
  } catch {
    // An unknown currency code still shows a correct number rather than NaN.
    return `${major.toFixed(exponent)} ${currency}`;
  }
}

/**
 * Minor units as text for an input box, in the currency's own decimals.
 *
 * `(minor / 100).toFixed(2)` was written in seven places and is wrong for
 * every three-decimal currency: 1500 fils is 1.500 JOD, and shown as "15.00"
 * it was re-parsed as 15,000 — ten times the amount, saved without anyone
 * touching the field.
 */
export function minorToInput(minor: number, currency: string): string {
  const exponent = EXPONENT[currency] ?? 2;
  return (minor / 10 ** exponent).toFixed(exponent);
}

/** Parse user input into minor units. Rejects anything that is not a number. */
export function parseToMinor(input: string, currency: string): number | null {
  const exponent = EXPONENT[currency] ?? 2;
  const normalised = input
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[\s,]/g, '');
  if (!/^\d+(\.\d+)?$/.test(normalised)) return null;
  const [whole = '0', frac = ''] = normalised.split('.');
  const padded = (frac + '0'.repeat(exponent)).slice(0, exponent);
  return Number(whole) * 10 ** exponent + Number(padded || '0');
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
